import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { getNewsletterSubscribers } from '../../lib/supabase';

const NewsletterManager: React.FC = () => {
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    try {
      const data = await getNewsletterSubscribers();
      setSubscribers(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const activeCount = subscribers.filter((s) => s.is_active).length;
  const inactiveCount = subscribers.length - activeCount;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Impact", "Arial Black", sans-serif',
            fontWeight: 900,
            fontSize: '2rem',
            textTransform: 'uppercase',
            color: '#1a1a1a',
          }}
        >
          Newsletter Subscribers
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 0,
            border: '2px solid #dc143c',
          }}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper sx={{ p: 3, borderRadius: 0, border: '3px solid #4caf50', bgcolor: '#1a1a1a' }}>
          <Typography variant="h6" sx={{ color: '#4caf50', fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase', mb: 1 }}>
            Active Subscribers
          </Typography>
          <Typography variant="h2" sx={{ color: '#4caf50', fontFamily: '"Impact", "Arial Black", sans-serif', fontWeight: 900 }}>{activeCount}</Typography>
        </Paper>
        <Paper sx={{ p: 3, borderRadius: 0, border: '3px solid #f44336', bgcolor: '#1a1a1a' }}>
          <Typography variant="h6" sx={{ color: '#f44336', fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase', mb: 1 }}>
            Unsubscribed
          </Typography>
          <Typography variant="h2" sx={{ color: '#f44336', fontFamily: '"Impact", "Arial Black", sans-serif', fontWeight: 900 }}>{inactiveCount}</Typography>
        </Paper>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 0, border: '2px solid #dc143c', bgcolor: 'white' }}>
        <Table sx={{ bgcolor: 'white' }}>
          <TableHead sx={{ bgcolor: '#1a1a1a' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Subscribed Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Unsubscribed Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ bgcolor: 'white' }}>
            {subscribers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No subscribers yet
                </TableCell>
              </TableRow>
            ) : (
              subscribers.map((subscriber) => (
                <TableRow
                  key={subscriber.id}
                  sx={{
                    bgcolor: 'white',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    }
                  }}
                >
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif', fontWeight: 600 }}>{subscriber.email}</TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif' }}>{subscriber.full_name || '-'}</TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif' }}>{formatDate(subscriber.subscribed_at)}</TableCell>
                  <TableCell>
                    {subscriber.is_active ? (
                      <Chip
                        icon={<ActiveIcon />}
                        label="Active"
                        size="small"
                        color="success"
                      />
                    ) : (
                      <Chip
                        icon={<InactiveIcon />}
                        label="Unsubscribed"
                        size="small"
                        color="error"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {subscriber.unsubscribed_at
                      ? formatDate(subscriber.unsubscribed_at)
                      : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NewsletterManager;